import React from 'react';
import { Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { isValid, parseISO } from 'date-fns';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';
import { resolveDisplayedRequestStatus } from 'src/features/requests/request.helper';

// Structural shape — matches both `RequestsTableResponse` (table rows) and
// `RequestResponse` (preview sheet / detail page), whose status fields differ
// in nominal type (`RequestStatus` literal vs `string`) but read identically
// here. Aligns with `resolveDisplayedRequestStatus`.
type Props = {
  request: {
    status?: string | null;
    flow?: string | null;
    requested_attention_at?: string | null;
    requested_reedit_at?: string | null;
    reedit_count?: number | null;
    has_unresolved_reedit?: boolean | null;
    archived_at?: string | null;
    files_delete_at?: string | null;
    files_deleted_at?: string | null;
    auto_approves_at?: string | null;
  };
  size?: 'sm' | 'default';
  /**
   * Table rows: the File Retention line the client sees under the pill in
   * Space ("Auto-approves in N days", "Available until <date> · N days left",
   * "Files were deleted on <date>"), so the team reads the same (#2756).
   */
  showRetention?: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const utcDay = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

// Days from today to a retention date, counted in UTC like every retention
// date (the API sends them at UTC midnight); null when it isn't a date.
const utcDaysUntil = (value: string): number | null => {
  const date = parseISO(value);
  return isValid(date) ? Math.round((utcDay(date) - utcDay(new Date())) / DAY_MS) : null;
};

// One formatter per language, not one per row and render.
const utcDateFormatters = new Map<string, Intl.DateTimeFormat>();

// Retention dates are whole days, shown as stored (UTC) - same as Space.
const formatUtcDate = (value: string, language = ''): string => {
  const date = parseISO(value);
  if (!isValid(date)) {
    return '';
  }
  let formatter = utcDateFormatters.get(language);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(language || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    utcDateFormatters.set(language, formatter);
  }

  return formatter.format(date);
};

// Shown from day 20 of the 30, as in Space.
const AUTO_APPROVE_NOTICE_DAYS = 10;

const MUTED = 'tw-text-muted-foreground';

/**
 * The File Retention line under a table row's pill, as the client sees it in
 * Space - or null when there is none, so the row keeps the plain pill.
 */
const retentionNote = (
  request: Props['request'],
  displayedStatus: string,
  t: TFunction,
  language: string,
): { text: string; tone: string } | null => {
  // Deleted files are permanent news, whatever the pill shows.
  if (request.files_deleted_at) {
    return {
      text: t('files_were_deleted_on', {
        date: formatUtcDate(request.files_deleted_at, language),
        defaultValue: 'Files were deleted on {{date}}',
      }),
      tone: MUTED,
    };
  }

  if (displayedStatus === 'delivered' && request.auto_approves_at) {
    const days = utcDaysUntil(request.auto_approves_at);
    if (days === null || days < 0 || days > AUTO_APPROVE_NOTICE_DAYS) {
      return null;
    }
    return {
      text:
        days === 0
          ? t('auto_approves_today', { defaultValue: 'Auto-approves today' })
          : t('auto_approves_in_days', {
              count: days,
              defaultValue_one: 'Auto-approves in {{count}} day',
              defaultValue_other: 'Auto-approves in {{count}} days',
            }),
      tone: MUTED,
    };
  }

  if (displayedStatus === 'archived' && request.files_delete_at) {
    const days = utcDaysUntil(request.files_delete_at);
    // Past the date: the nightly deletion hasn't caught up yet.
    if (days === null || days < 0) {
      return null;
    }
    const on = formatUtcDate(request.files_delete_at, language);
    return {
      // Same wording as Space: "Available until", so it reads apart from
      // "Files were deleted on" (#2752).
      text:
        days === 0
          ? t('files_available_until_today', { defaultValue: 'Available until today' })
          : t('files_available_until_days_left', {
              count: days,
              date: on,
              defaultValue_one: 'Available until {{date}} · {{count}} day left',
              defaultValue_other: 'Available until {{date}} · {{count}} days left',
            }),
      tone: days <= 7 ? 'tw-text-red-600' : days <= 30 ? 'tw-text-amber-600' : MUTED,
    };
  }

  return null;
};

/**
 * Status pill for a row on the admin requests table.
 *
 * The pill itself absorbs the "Needs Attention" / "Needs Re-edit" signal the
 * now-removed `status_details` column used to carry.
 *
 * A `Repeat` icon trails inside the pill while the row is in an ACTIVE re-edit
 * round. The signal is `requested_reedit_at`: be-core sets it when the customer
 * requests a re-edit and keeps it set through Needs Re-edit → Editing → Ready
 * for Review (RequestManager), clearing it on approval/undo. So the icon
 * "only begins appearing" once the request hits Needs Re-edit and then trails
 * the round (#2468 Karina), exactly as #2442/#2443/#2462 intended.
 *
 * Two signals drive it, OR'd together:
 *  - the *active* `requested_reedit_at` — set while a round is in flight and
 *    kept through Needs Re-edit → Editing → Ready for Review, cleared on
 *    approval/undo; and
 *  - the *historical* `reedit_count` (be-core's lifetime counter, #2442) but
 *    ONLY while the row is "Ready for review" (`delivered`).
 *
 * The historical branch exists because Cancel/Undo clears `requested_reedit_at`
 * the instant a client starts then cancels a fresh re-edit, wiping the trail
 * off a row that genuinely completed an earlier cycle and is still awaiting
 * review (#2548). `reedit_count` survives that cancel (be-core never clears
 * it), so it restores the indicator. It is gated to `delivered` on purpose:
 * `reedit_count` stays >0 forever after any past re-edit, and ungated it lit
 * the icon next to a plain "Queued" row (the DOOR #1 bug in #2468). Gating
 * to "Ready for review" keeps the #2468 fix while satisfying #2548.
 *
 * Suppressed while the pill shows "Needs Attention" — that overlay is
 * urgency-first and supersedes the re-edit icon (#2462); the underlying
 * re-edit state is unchanged, so the icon resumes once attention clears.
 */
const RequestStatusCell: React.FC<Props> = ({ request, size = 'sm', showRetention = false }) => {
  const { t, i18n } = useTranslation();
  const displayedStatus = resolveDisplayedRequestStatus(request);
  const showReeditIndicator =
    (!!request.requested_reedit_at ||
      (!!request.reedit_count && request.status === 'delivered')) &&
    request.status !== 'complete' &&
    request.status !== 'archived' &&
    displayedStatus !== 'needs_attention';

  const badge = (
    <RequestStatusBadge
      size={size}
      variant={displayedStatus}
      iconPrefix={
        showReeditIndicator ? (
          // No color class — inherits the badge's text color (currentColor) so
          // it tints to whatever stage the round is in (red on Needs Re-edit,
          // etc.). Shrunk to 12px to match the inline-icon scale on the
          // customer FE pill. Static (no spin): signals "went through a
          // re-edit cycle", not "loading" (#164).
          <Repeat className={'tw-size-3 tw-shrink-0'} aria-hidden={'true'} />
        ) : undefined
      }
    />
  );

  const note = showRetention ? retentionNote(request, displayedStatus, t, i18n.language) : null;
  if (!note) {
    return badge;
  }

  return (
    <div className={'tw-flex tw-flex-col tw-items-start tw-gap-1'}>
      {badge}
      <span className={`tw-text-xs tw-whitespace-nowrap ${note.tone}`}>{note.text}</span>
    </div>
  );
};

export { RequestStatusCell };
